import { useState } from "react";
import { useConsent } from "@/hooks/useConsent";

const GOLD = "#B8977E";
const GOLD_DARK = "#a07d63";

/**
 * CNIL 2024 compliant cookie banner.
 * - 3 buttons ("Tout accepter" / "Tout refuser" / "Personnaliser") with EQUAL visual weight.
 * - No dark pattern, no default-accept, no × dismissal.
 * - Analytics + Advertising default OFF in the personalize modal (opt-in).
 */
const buttonBase = {
  padding: "0.75rem 1.5rem",
  borderRadius: "0.75rem",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all .2s ease",
  minWidth: 140,
};

const primaryBtn = {
  ...buttonBase,
  background: GOLD,
  border: `1.5px solid ${GOLD}`,
  color: "#fff",
};

const outlineBtn = {
  ...buttonBase,
  background: "transparent",
  border: `1.5px solid ${GOLD}`,
  color: GOLD,
};

const Toggle = ({ checked, onChange, disabled }) => (
  <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1 }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => !disabled && onChange(e.target.checked)}
      disabled={disabled}
      style={{ opacity: 0, width: 0, height: 0 }}
    />
    <span style={{
      position: "absolute", inset: 0, borderRadius: 24,
      background: checked ? GOLD : "#d8d4cf",
      transition: "background .2s ease",
    }} />
    <span style={{
      position: "absolute", top: 2, left: checked ? 22 : 2,
      width: 20, height: 20, background: "#fff", borderRadius: "50%",
      transition: "left .2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    }} />
  </label>
);

export const CookieBanner = () => {
  const { isBannerOpen, updateConsent } = useConsent();
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  if (!isBannerOpen) return null;

  const acceptAll = () => updateConsent({ necessary: true, analytics: true, advertising: true });
  const rejectAll = () => updateConsent({ necessary: true, analytics: false, advertising: false });
  const savePreferences = () => updateConsent({ necessary: true, analytics, advertising });

  return (
    <>
      {/* Bandeau bas */}
      <div
        data-testid="cookie-banner"
        role="dialog"
        aria-label="Consentement aux cookies"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9998,
          background: "#FFFFFF", borderTop: `3px solid ${GOLD}`,
          boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
          padding: "1.5rem 2rem",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr auto", gap: "1.5rem", alignItems: "center" }} className="cb-grid">
          <div>
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.1rem", fontWeight: 700, color: "#1A1A1A", margin: "0 0 0.35rem" }}>
              🍪 Nous utilisons des cookies
            </p>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: "#555", lineHeight: 1.5, margin: 0 }}>
              Ce site utilise des cookies pour améliorer votre expérience et mesurer notre audience.{" "}
              <a
                href="/politique-cookies"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, fontWeight: 600, textDecoration: "underline" }}
                data-testid="cookie-banner-learn-more"
              >
                En savoir plus →
              </a>
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }} className="cb-btns">
            <button
              type="button"
              data-testid="cookie-banner-personalize"
              onClick={() => setShowPersonalize(true)}
              style={outlineBtn}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#faf6f2"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Personnaliser
            </button>
            <button
              type="button"
              data-testid="cookie-banner-reject-all"
              onClick={rejectAll}
              style={primaryBtn}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_DARK; e.currentTarget.style.borderColor = GOLD_DARK; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.borderColor = GOLD; }}
            >
              Tout refuser
            </button>
            <button
              type="button"
              data-testid="cookie-banner-accept-all"
              onClick={acceptAll}
              style={primaryBtn}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_DARK; e.currentTarget.style.borderColor = GOLD_DARK; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.borderColor = GOLD; }}
            >
              Tout accepter
            </button>
          </div>
        </div>
        <style>{`
          @media (max-width: 767px) {
            .cb-grid { grid-template-columns: 1fr !important; }
            .cb-btns { justify-content: stretch !important; flex-direction: column !important; }
            .cb-btns button { width: 100% !important; }
          }
        `}</style>
      </div>

      {/* Modal Personnaliser */}
      {showPersonalize && (
        <div
          role="dialog"
          aria-label="Personnaliser mes cookies"
          data-testid="cookie-personalize-modal"
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(26,26,26,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPersonalize(false); }}
        >
          <div style={{ background: "#fff", borderRadius: "1rem", maxWidth: 500, width: "100%", padding: "2rem", boxShadow: "0 24px 60px rgba(0,0,0,0.25)", fontFamily: "'DM Sans',sans-serif" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.35rem", fontWeight: 700, color: "#1A1A1A", margin: 0 }}>
                Personnaliser mes préférences
              </h2>
              <button
                type="button"
                onClick={() => setShowPersonalize(false)}
                aria-label="Fermer"
                style={{ background: "transparent", border: "none", color: "#999", cursor: "pointer", fontSize: "1.3rem", padding: 0 }}
              >
                ✕
              </button>
            </div>

            {[
              {
                key: "necessary",
                icon: "🔒", title: "Cookies nécessaires",
                desc: "Indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.",
                checked: true, disabled: true, onChange: () => {},
                testid: "toggle-necessary",
              },
              {
                key: "analytics",
                icon: "📊", title: "Cookies analytiques",
                desc: "Google Analytics : mesure d'audience anonyme. Aidez-nous à améliorer le site.",
                checked: analytics, disabled: false, onChange: setAnalytics,
                testid: "toggle-analytics",
              },
              {
                key: "advertising",
                icon: "🎯", title: "Cookies publicitaires",
                desc: "Meta Pixel : mesure de nos publicités Facebook et Instagram.",
                checked: advertising, disabled: false, onChange: setAdvertising,
                testid: "toggle-advertising",
              },
            ].map((row, i) => (
              <div key={row.key} style={{ padding: "1rem 0", borderTop: i === 0 ? "none" : "1px solid #f0e6dc" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", marginBottom: "0.35rem" }}>
                  <p style={{ fontWeight: 700, color: "#1A1A1A", fontSize: "0.95rem", margin: 0 }}>
                    <span style={{ marginRight: "0.5rem" }}>{row.icon}</span>{row.title}
                  </p>
                  <div data-testid={row.testid}>
                    <Toggle checked={row.checked} onChange={row.onChange} disabled={row.disabled} />
                  </div>
                </div>
                <p style={{ fontSize: "0.82rem", color: "#666", lineHeight: 1.5, margin: 0 }}>
                  {row.desc}
                </p>
              </div>
            ))}

            <button
              type="button"
              data-testid="cookie-personalize-save"
              onClick={() => { savePreferences(); setShowPersonalize(false); }}
              style={{ ...primaryBtn, width: "100%", marginTop: "1.5rem", padding: "0.85rem" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_DARK; e.currentTarget.style.borderColor = GOLD_DARK; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.borderColor = GOLD; }}
            >
              Enregistrer mes préférences
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
